<template>
  <!-- Table Container -->
  <table class="w-full border-separate border-spacing-0">
    <thead class="sticky -top-2 z-10">
      <tr class="child:border-b child:border-gray-200 bg-gray-50">
        <th class="text-left py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.plant') }}
        </th>

        <th class="text-left py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.supplier') }}
        </th>

        <th class="text-left py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.payStandby') }}
        </th>

        <th class="text-left py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.hirePeriod') }}
        </th>

        <th class="text-right py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.rate') }}
        </th>

        <th class="text-right py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.period') }}
        </th>

        <th class="text-right py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.accrual') }}
        </th>

        <th class="text-right py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.paidToDate') }}
        </th>

        <th class="text-right py-3 px-4 font-semibold text-gray-900 text-xs tracking-wider uppercase">
          {{ $t('SelfPerform.PlantDashboard.PlantList.table.totalCost') }}
        </th>
      </tr>

      <!-- Summary Totals Row -->
      <tr
        class="sticky top-8 bg-white child:border-b child:border-gray-200 z-10"
        data-test="totals"
      >
        <td
          colspan="5"
          class="py-3 px-4"
        >
          <span class="font-semibold text-gray-900">{{ $t('SelfPerform.PlantDashboard.PlantList.table.totals') }}</span>
        </td>

        <td
          class="py-3 px-4 text-right font-semibold text-gray-900"
          data-test="totals-wet"
        >
          {{
            $tn(
              $props.plants.filter((plant) => plant.wet)
                .reduce((total, value) => total + value.totalHours, 0))
          }} {{ $t('SelfPerform.PlantDashboard.PlantList.table.hrs') }}

          {{
            $tn(
              $props.plants.filter((plant) => !plant.wet)
                .reduce((total, value) => total + value.totalDays, 0))
          }} {{ $t('SelfPerform.PlantDashboard.PlantList.table.days') }}
        </td>

        <td
          class="py-3 px-4 text-right font-semibold text-gray-900"
          data-test="totals-accrual"
        >
          {{ $tcc($props.plants.reduce((total, value) => total + value.totalAccrual, 0)) }}
        </td>

        <td
          class="py-3 px-4 text-right font-semibold text-gray-900"
          data-test="totals-paid-to-date"
        >
          {{ $tcc($props.plants.reduce((total, value) => total + value.paidToDate, 0)) }}
        </td>

        <td
          class="py-3 px-4 text-right font-semibold text-blue-600"
          data-test="totals-total"
        >
          {{
            $tcc(
              $props.plants.reduce((total, value) => total + (value.paidToDate + value.totalAccrual), 0)
            )
          }}
        </td>
      </tr>
    </thead>

    <tbody class="bg-white">
      <PlantListRow
        v-for="plant in $props.plants"
        :key="plant.id"
        :plant="plant"
        @show-off-hired="($event: Id) => showHirePeriodModal($event)"
        @show-update-modal="($event: Id) => showUpdateModal($event)"
      />
    </tbody>
  </table>

  <!-- Update Modal -->
  <div>
    <PlantSaveModal
      v-if="show"
      :project-id="$props.projectId"
      :plant-id="selectedPlantId"
      @close="show = false"
    />

    <NewHirePeriodModal
      v-if="showHirePeriod"
      :plant-id="selectedPlantId"
      @close="showHirePeriod = false"
    />
  </div>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import Plant from '@/models/Plant';
import { ref } from 'vue';
import NewHirePeriodModal from './NewHirePeriodModal.vue';
import PlantListRow from './PlantListRow.vue';
import PlantSaveModal from './PlantSaveModal.vue';

defineProps<{
  projectId: string;
  plants: Plant[];
}>();

const show = ref(false);
const selectedPlantId = ref(undefined);
const showHirePeriod = ref(false);

const showHirePeriodModal = (id: Id) => {
  selectedPlantId.value = id;
  showHirePeriod.value = true;
};

const showUpdateModal = (id: Id) => {
  selectedPlantId.value = id;
  show.value = true;
};
</script>

<style scoped></style>
